import GlobalTable from 'components/shared/table/GlobalTable'
import GridSpecialPopups from 'pages/grid/components/GridSpecialPopups';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom'
import { exportToXlsx } from 'utils/helpers';
import { useGridColumns } from 'pages/grid/hooks/useGridColumns';
import { useGridDataSourceFlow } from 'pages/grid/hooks/useGridDataSourceFlow';
import { useGridTableCapabilities } from 'pages/grid/hooks/useGridTableCapabilities';
import { useGridDeleteRow } from 'pages/grid/hooks/useGridDeleteRow';
import { useGridRowActions } from 'pages/grid/hooks/useGridRowActions';
import { useGridUpload } from 'pages/grid/hooks/useGridUpload';
import { TraceWidget } from 'components/Trace';
import LayoutWidgets from 'pages/grid/components/LayoutWidgets';
import YearLossEvolutionChart from 'pages/grid/components/YearLossEvolutionChart';
import MenuDefPopup from 'pages/grid/components/MenuDefPopup';
import { canIDoThis } from 'utils/helpers/rightsManager';
import { useAuth } from 'utils/hooks/useAuth';

type TableRecord = Record<string, any>;

const WIDGET_TYPE_TO_INPUT: Record<string, string> = {
  BasicWidget: 'BasicWidgetInput',
  TableWidget: 'TableWidgetInput',
  ObjectWidget: 'ObjectWidgetInput',
  ChartWidget: 'ChartWidgetInput',
  ColorCard: 'ColorCardInput',
  TraceWidget: 'TraceWidgetInput',
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]';

const flattenRecord = (
  record: Record<string, unknown>,
  prefix = ''
): Record<string, unknown> => {
  return Object.entries(record).reduce<Record<string, unknown>>((acc, [key, value]) => {
    const composedKey = prefix ? `${prefix}.${key}` : key;

    if (isPlainObject(value)) {
      Object.assign(acc, flattenRecord(value, composedKey));
      return acc;
    }

    if (Array.isArray(value)) {
      acc[composedKey] = value
        .map((item) => (isPlainObject(item) || Array.isArray(item) ? JSON.stringify(item) : item))
        .join(', ');
      return acc;
    }

    acc[composedKey] = value;
    return acc;
  }, {});
};

export const GridPage: React.FC = () => {
  const { layoutId } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const canModifyLayout = canIDoThis({ role }, 'layout_modify');
  const isTraceLayoutById = Number(layoutId) === 28;

  // layout/1 is not a valid standalone page, send users typing it manually to the default layout
  useEffect(() => {
    if (layoutId === '1') {
      navigate('/layout/6', { replace: true });
    }
  }, [layoutId, navigate]);
  const [isTvaCreateOpen, setIsTvaCreateOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<TableRecord | null>(null);
  const [isEnergyEditOpen, setIsEnergyEditOpen] = useState(false);
  const [editingEnergyRow, setEditingEnergyRow] = useState<TableRecord | null>(null);
  const [isLocaleEditOpen, setIsLocaleEditOpen] = useState(false);
  const [editingLocaleRow, setEditingLocaleRow] = useState<TableRecord | null>(null);
  const [isEnvironmentEditOpen, setIsEnvironmentEditOpen] = useState(false);
  const [editingEnvironmentRow, setEditingEnvironmentRow] = useState<TableRecord | null>(null);
  const [isAdminUsersEditOpen, setIsAdminUsersEditOpen] = useState(false);
  const [editingAdminUsersRow, setEditingAdminUsersRow] = useState<TableRecord | null>(null);
  const [isAccessoryGroupEditOpen, setIsAccessoryGroupEditOpen] = useState(false);
  const [editingAccessoryGroupRow, setEditingAccessoryGroupRow] = useState<TableRecord | null>(null);
  const [isYearLossEditOpen, setIsYearLossEditOpen] = useState(false);
  const [editingYearLossRow, setEditingYearLossRow] = useState<TableRecord | null>(null);
  const [isAnnexEditOpen, setIsAnnexEditOpen] = useState(false);
  const [editingAnnexRow, setEditingAnnexRow] = useState<TableRecord | null>(null);
  const [isFaqEditOpen, setIsFaqEditOpen] = useState(false);
  const [editingFaqRow, setEditingFaqRow] = useState<TableRecord | null>(null);
  const [isVersionEditOpen, setIsVersionEditOpen] = useState(false);
  const [editingVersionRow, setEditingVersionRow] = useState<TableRecord | null>(null);
  const [isSatisfactionViewOpen, setIsSatisfactionViewOpen] = useState(false);
  const [viewingSatisfactionRow, setViewingSatisfactionRow] = useState<TableRecord | null>(null);
  const [isParameterEditOpen, setIsParameterEditOpen] = useState(false);
  const [editingParameterRow, setEditingParameterRow] = useState<TableRecord | null>(null);
  const [isDiscountManagementEditOpen, setIsDiscountManagementEditOpen] = useState(false);
  const [editingDiscountManagementRow, setEditingDiscountManagementRow] = useState<TableRecord | null>(null);
  const [isAccessoryEditOpen, setIsAccessoryEditOpen] = useState(false);
  const [editingAccessoryRow, setEditingAccessoryRow] = useState<TableRecord | null>(null);
  const [isAccessorySuperEditOpen, setIsAccessorySuperEditOpen] = useState(false);
  const [editingAccessorySuperRow, setEditingAccessorySuperRow] = useState<TableRecord | null>(null);
  const [isHpSuperEditOpen, setIsHpSuperEditOpen] = useState(false);
  const [editingHpSuperRow, setEditingHpSuperRow] = useState<TableRecord | null>(null);
  const [isHpEditOpen, setIsHpEditOpen] = useState(false);
  const [editingHpRow, setEditingHpRow] = useState<TableRecord | null>(null);
  const [isTutorialEditOpen, setIsTutorialEditOpen] = useState(false);
  const [editingTutorialRow, setEditingTutorialRow] = useState<TableRecord | null>(null);
  const [deletingRow, setDeletingRow] = useState<TableRecord | null>(null);
  const [isMenuDefCreateOpen, setIsMenuDefCreateOpen] = useState(false);
  const [editingMenuDefRow, setEditingMenuDefRow] = useState<TableRecord | null>(null);
  const {
    envLang,
    layoutItems,
    isDashboardLayout,
    isTutorialsLayout,
    isAdminTutorialsLayout,
    isFaqTable: isFaqTableFlow,
    isVersionsTable,
    faqLoading,
    faqFetching,
    refetchFaq,
    refetchAdminUsers,
    refetchEnvironments,
    refetchVersions,
    versionsLoading,
    versionsFetching,
    tableWidget,
    tableTitle,
    layoutPageTitle,
    selectedDataSource,
    rows,
    uploadEndpoint,
    createDatasourceRequest,
    updateDatasourceRequest,
    trashDatasourceRequest,
    refetchDatasource,
    layoutLoading,
    layoutFetching,
    dataSourcesLoading,
    menuTableLoading,
    menuTableFetching,
    refetchMenu,
    tutorialsLoading,
    tutorialsFetching,
    refetchTutorials,
    tableLoading,
    tableFetching,
  } = useGridDataSourceFlow(layoutId);

  const columns = useGridColumns({ tableWidget, rows });

  const {
    isTvaTable,
    isEnergyTable,
    isEnergySuperTable,
    isLocaleTable,
    isEnvironmentTable,
    isAdminUsersTable,
    isAccessoryGroupTable,
    isYearLossTable,
    isAnnexTable,
    isFaqTable,
    isVersionsTable: isVersionsTableCapabilities,
    isTraceTable,
    isSatisfactionTable,
    isParameterTable,
    isDiscountManagementTable,
    isMenuDefsTable,
    isAccessoryEditTable,
    isAccessorySuperTable,
    isHpSuperTable,
    isHpTable,
    canCreate,
    canEdit,
    canDelete,
    canOpenTvaCreate,
    canOpenTvaEdit,
    canOpenEnergyCreate,
    canOpenEnergyEdit,
    canOpenLocaleCreate,
    canOpenLocaleEdit,
    canOpenEnvironmentCreate,
    canOpenEnvironmentEdit,
    canOpenAdminUsersCreate,
    canOpenAdminUsersEdit,
    canOpenAccessoryGroupCreate,
    canOpenAccessoryGroupEdit,
    canOpenYearLossEdit,
    canOpenAnnexCreate,
    canOpenAnnexEdit,
    canOpenFaqCreate,
    canOpenFaqEdit,
    canOpenVersionCreate,
    canOpenVersionEdit,
    canOpenSatisfactionView,
    canOpenParameterCreate,
    canOpenParameterEdit,
    canOpenDiscountManagementCreate,
    canOpenDiscountManagementEdit,
    canOpenMenuDefEdit,
    canOpenAccessoryEdit,
    canOpenAccessorySuperCreate,
    canOpenAccessorySuperEdit,
    canOpenHpSuperCreate,
    canOpenHpSuperEdit,
    canOpenHpEdit,
  } = useGridTableCapabilities({
    tableTitle,
    layoutPageTitle,
    tableWidgetName: tableWidget?.name,
    tableWidgetFields: tableWidget?.fields,
    selectedDataSource,
    isDashboardLayout,
    isTutorialsLayout,
    isAdminTutorialsLayout,
  });

  const openPrimaryCreatePopup = () => {
    if (canOpenTvaCreate) {
      setEditingRow(null);
      setIsTvaCreateOpen(true);
      return true;
    }
    if (isTutorialsLayout && canCreate) {
      setEditingTutorialRow(null);
      setIsTutorialEditOpen(true);
      return true;
    }

    if (canOpenLocaleCreate) {
      setEditingLocaleRow(null);
      setIsLocaleEditOpen(true);
      return true;
    }

    if (canOpenEnvironmentCreate) {
      setEditingEnvironmentRow(null);
      setIsEnvironmentEditOpen(true);
      return true;
    }

    if (canOpenAdminUsersCreate) {
      setEditingAdminUsersRow(null);
      setIsAdminUsersEditOpen(true);
      return true;
    }

    if (canOpenEnergyCreate) {
      setEditingEnergyRow(null);
      setIsEnergyEditOpen(true);
      return true;
    }

    if (canOpenAccessoryGroupCreate) {
      setEditingAccessoryGroupRow(null);
      setIsAccessoryGroupEditOpen(true);
      return true;
    }

    if (canOpenAnnexCreate) {
      setEditingAnnexRow(null);
      setIsAnnexEditOpen(true);
      return true;
    }

    return false;
  };

  const openSecondaryCreatePopup = () => {
    if (canOpenFaqCreate) {
      setEditingFaqRow(null);
      setIsFaqEditOpen(true);
      return true;
    }

    if (canOpenVersionCreate) {
      setEditingVersionRow(null);
      setIsVersionEditOpen(true);
      return true;
    }

    if (canOpenParameterCreate) {
      setEditingParameterRow(null);
      setIsParameterEditOpen(true);
      return true;
    }

    if (canOpenDiscountManagementCreate) {
      setEditingDiscountManagementRow(null);
      setIsDiscountManagementEditOpen(true);
      return true;
    }

    if (canOpenHpSuperCreate) {
      setEditingHpSuperRow(null);
      setIsHpSuperEditOpen(true);
      return true;
    }

    if (canOpenAccessorySuperCreate) {
      setEditingAccessorySuperRow(null);
      setIsAccessorySuperEditOpen(true);
      return true;
    }

    if (isMenuDefsTable && canCreate) {
      setEditingMenuDefRow(null);
      setIsMenuDefCreateOpen(true);
      return true;
    }

    return false;
  };

  const handleNewClick = () => {
    if (openPrimaryCreatePopup()) return;
    if (openSecondaryCreatePopup()) return;

    console.debug("Create action requested", {
      layoutId,
      table: tableWidget?.table,
      title: tableTitle,
    });
  };

  const { deleteRow, requestDeleteRow } = useGridDeleteRow({
    envLang,
    isDashboardLayout,
    isEnvironmentTable,
    isAdminUsersTable,
    isVersionsTable,
    isTvaTable,
    isAnnexTable,
    isParameterTable,
    isDiscountManagementTable,
    isFaqTable,
    isTutorialsLayout,
    isAdminTutorialsLayout,
    refetchDatasource,
    refetchTutorials,
    refetchEnvironments,
    refetchVersions,
    refetchAdminUsers,
    refetchMenu,
    refetchFaq,
    selectedDataSource,
    trashDatasourceRequest,
    setDeletingRow,
  });

  const rowActions = useGridRowActions({
    canDelete,
    canEdit,
    isTvaTable,
    isEnergyTable,
    isLocaleTable,
    isEnvironmentTable,
    isAdminUsersTable,
    isAccessoryGroupTable,
    isYearLossTable,
    isAnnexTable,
    isFaqTable,
    isVersionsTable: isVersionsTableCapabilities,
    isSatisfactionTable,
    isParameterTable,
    isDiscountManagementTable,
    isAccessoryEditTable,
    isAccessorySuperTable,
    isHpSuperTable,
    isHpTable,
    isTutorialsLayout,
    canOpenTvaEdit,
    canOpenEnergyEdit,
    canOpenLocaleEdit,
    canOpenEnvironmentEdit,
    canOpenAdminUsersEdit,
    canOpenAccessoryGroupEdit,
    canOpenYearLossEdit,
    canOpenAnnexEdit,
    canOpenFaqEdit,
    canOpenVersionEdit,
    canOpenSatisfactionView,
    canOpenParameterEdit,
    canOpenDiscountManagementEdit,
    canOpenMenuDefEdit,
    canOpenAccessoryEdit,
    canOpenAccessorySuperEdit,
    canOpenHpSuperEdit,
    canOpenHpEdit,
    onOpenTvaEdit: (row) => {
      setEditingRow(row);
      setIsTvaCreateOpen(true);
    },
    onOpenEnergyEdit: (row) => {
      setEditingEnergyRow(row);
      setIsEnergyEditOpen(true);
    },
    onOpenLocaleEdit: (row) => {
      setEditingLocaleRow(row);
      setIsLocaleEditOpen(true);
    },
    onOpenEnvironmentEdit: (row) => {
      setEditingEnvironmentRow(row);
      setIsEnvironmentEditOpen(true);
    },
    onOpenAdminUsersEdit: (row) => {
      setEditingAdminUsersRow(row);
      setIsAdminUsersEditOpen(true);
    },
    onOpenAccessoryGroupEdit: (row) => {
      setEditingAccessoryGroupRow(row);
      setIsAccessoryGroupEditOpen(true);
    },
    onOpenYearLossEdit: (row) => {
      setEditingYearLossRow(row);
      setIsYearLossEditOpen(true);
    },
    onOpenAnnexEdit: (row) => {
      setEditingAnnexRow(row);
      setIsAnnexEditOpen(true);
    },
    onOpenFaqEdit: (row) => {
      setEditingFaqRow(row);
      setIsFaqEditOpen(true);
    },
    onOpenVersionEdit: (row) => {
      setEditingVersionRow(row);
      setIsVersionEditOpen(true);
    },
    onOpenSatisfactionView: (row) => {
      setViewingSatisfactionRow(row);
      setIsSatisfactionViewOpen(true);
    },
    onOpenParameterEdit: (row) => {
      setEditingParameterRow(row);
      setIsParameterEditOpen(true);
    },
    onOpenDiscountManagementEdit: (row) => {
      setEditingDiscountManagementRow(row);
      setIsDiscountManagementEditOpen(true);
    },
    onOpenMenuDefEdit: (row) => {
      setEditingMenuDefRow(row);
      setIsMenuDefCreateOpen(true);
    },
    onOpenAccessoryEdit: (row) => {
      setEditingAccessoryRow(row);
      setIsAccessoryEditOpen(true);
    },
    onOpenAccessorySuperEdit: (row) => {
      setEditingAccessorySuperRow(row);
      setIsAccessorySuperEditOpen(true);
    },
    onOpenHpSuperEdit: (row) => {
      setEditingHpSuperRow(row);
      setIsHpSuperEditOpen(true);
    },
    onOpenHpEdit: (row) => {
      setEditingHpRow(row);
      setIsHpEditOpen(true);
    },
    onOpenTutorialEdit: (row) => {
      setEditingTutorialRow(row);
      setIsTutorialEditOpen(true);
    },
    onDelete: requestDeleteRow,
    layoutId,
    tableWidgetTable: tableWidget?.table,
    tableTitle,
  });

  const handleDownload = () => {
    if (!rows.length) return;
    const exportRows = rows.map(({ key, ...rest }) => {
      if (!isSatisfactionTable) return rest;
      return flattenRecord(rest);
    });
    exportToXlsx(exportRows, tableTitle || "table");
  };

  const { uploadInputRef, handleUploadClick, handleUploadFileChange, isUploading } = useGridUpload({
    uploadEndpoint,
    envLang,
    tableTitle,
  });

  let tableRowsLoading = tableLoading || tableFetching;
  if (isDashboardLayout) {
    tableRowsLoading = menuTableLoading || menuTableFetching;
  } else if (isTutorialsLayout) {
    tableRowsLoading = tutorialsLoading || tutorialsFetching;
  } else if (isVersionsTable) {
    tableRowsLoading = versionsLoading || versionsFetching;
  } else if (isFaqTableFlow) {
    tableRowsLoading = faqLoading || faqFetching;
  }

  const isGridLoading = layoutLoading || layoutFetching || dataSourcesLoading || tableRowsLoading;

  const shouldShowTraceWidget = isTraceLayoutById || isTraceTable;
  const shouldShowWidgetLayout = layoutItems.length > 1 || layoutItems.some((item: any) => item?.widget?.type !== 'TableWidget');

  const editableWidget = useMemo(() => {
    const found = (Array.isArray(layoutItems) ? layoutItems : []).find((item: any) => {
      const widgetType = typeof item?.widget?.type === 'string' ? item.widget.type : '';
      return Boolean(item?.i) && Boolean(WIDGET_TYPE_TO_INPUT[widgetType]);
    });
    if (!found) return null;

    const widgetType = String(found.widget?.type ?? '');
    const widgetInputType = WIDGET_TYPE_TO_INPUT[widgetType];
    if (!widgetInputType) return null;

    return {
      itemId: String(found.i),
      widgetInputType,
    };
  }, [layoutItems]);

  const onEditCurrentWidget = () => {
    if (!layoutId || !editableWidget) return;
    navigate(`/layout/${layoutId}/widgets/${encodeURIComponent(editableWidget.widgetInputType)}?editWidgetId=${encodeURIComponent(editableWidget.itemId)}`);
  };

  return (
    <group data-height="auto" data-min-height="fit">
      {!shouldShowTraceWidget && shouldShowWidgetLayout && (
        <group data-direction="column" data-gap="10" data-space="10">
          <group
            data-border=""
            data-contain=""
            data-direction="column"
            data-wrap="no"
            data-height="autofit"
          >
            <LayoutWidgets items={layoutItems} layoutId={layoutId} rowActions={rowActions} />
          </group>
        </group>
      )}
      {shouldShowTraceWidget && (
        <group data-direction="column" data-gap="10" data-space="10">
          <group
            data-border=""
            data-contain=""
            data-direction="column"
            data-wrap="no"
            data-height="autofit"
          >

            <view className="table_cont" data-border="no" data-background="none">
              <TraceWidget fixedDataSource={selectedDataSource} />
            </view>
          </group>
        </group>
      )}
      {uploadEndpoint && (
        <input
          ref={(node) => {
            uploadInputRef.current = node;
          }}
          type="file"
          style={{ display: "none" }}
          onChange={handleUploadFileChange}
        />
      )}
      {/*<div>{process.env.REACT_APP_VERSION}</div>*/}
      {!shouldShowTraceWidget && !shouldShowWidgetLayout && (
        <>
          <GlobalTable<TableRecord>
            rowKey="key"
            columns={columns}
            data={rows}
            tableTitle={tableTitle || layoutPageTitle}
            mode="client"
            searchable
            searchPlaceholder="Search"
            variant="legacy"
            className={isAnnexTable ? "annex-grid-table" : undefined}
            scroll={isAnnexTable ? { x: true } : undefined}
            rowActions={rowActions}
            onNew={canCreate || isTvaTable || canOpenEnergyCreate || isLocaleTable || isEnvironmentTable || isAdminUsersTable || canOpenAccessoryGroupCreate || isAnnexTable || isFaqTable || isVersionsTableCapabilities || isParameterTable || isDiscountManagementTable || isAccessorySuperTable || isHpSuperTable || isTutorialsLayout ? handleNewClick : undefined}
            onUpload={uploadEndpoint ? handleUploadClick : undefined}
            isUploading={isUploading}
            onDownload={rows.length ? handleDownload : undefined}
            onEditWidget={!shouldShowTraceWidget && !shouldShowWidgetLayout && canModifyLayout && editableWidget ? onEditCurrentWidget : undefined}
            loading={isGridLoading || isUploading}
          />

          {isYearLossTable && rows.length > 1 && (
            <YearLossEvolutionChart rows={rows} title="yearloss_evolution" />
          )}
        </>
      )}

      <GridSpecialPopups
        deletingRow={deletingRow}
        onCloseDelete={() => {
          setDeletingRow(null);
        }}
        onConfirmDelete={() => {
          if (deletingRow) {
            deleteRow(deletingRow);
          }
        }}
        isTvaCreateOpen={isTvaCreateOpen}
        setIsTvaCreateOpen={setIsTvaCreateOpen}
        editingRow={editingRow}
        setEditingRow={setEditingRow}
        isEnergyEditOpen={isEnergyEditOpen}
        setIsEnergyEditOpen={setIsEnergyEditOpen}
        editingEnergyRow={editingEnergyRow}
        setEditingEnergyRow={setEditingEnergyRow}
        isEnergySuperTable={isEnergySuperTable}
        isLocaleEditOpen={isLocaleEditOpen}
        setIsLocaleEditOpen={setIsLocaleEditOpen}
        editingLocaleRow={editingLocaleRow}
        setEditingLocaleRow={setEditingLocaleRow}
        isEnvironmentEditOpen={isEnvironmentEditOpen}
        setIsEnvironmentEditOpen={setIsEnvironmentEditOpen}
        editingEnvironmentRow={editingEnvironmentRow}
        setEditingEnvironmentRow={setEditingEnvironmentRow}
        isAdminUsersEditOpen={isAdminUsersEditOpen}
        setIsAdminUsersEditOpen={setIsAdminUsersEditOpen}
        editingAdminUsersRow={editingAdminUsersRow}
        setEditingAdminUsersRow={setEditingAdminUsersRow}
        isAccessoryGroupEditOpen={isAccessoryGroupEditOpen}
        setIsAccessoryGroupEditOpen={setIsAccessoryGroupEditOpen}
        editingAccessoryGroupRow={editingAccessoryGroupRow}
        setEditingAccessoryGroupRow={setEditingAccessoryGroupRow}
        isYearLossEditOpen={isYearLossEditOpen}
        setIsYearLossEditOpen={setIsYearLossEditOpen}
        editingYearLossRow={editingYearLossRow}
        setEditingYearLossRow={setEditingYearLossRow}
        isAnnexEditOpen={isAnnexEditOpen}
        setIsAnnexEditOpen={setIsAnnexEditOpen}
        editingAnnexRow={editingAnnexRow}
        setEditingAnnexRow={setEditingAnnexRow}
        isFaqEditOpen={isFaqEditOpen}
        setIsFaqEditOpen={setIsFaqEditOpen}
        editingFaqRow={editingFaqRow}
        setEditingFaqRow={setEditingFaqRow}
        isVersionEditOpen={isVersionEditOpen}
        setIsVersionEditOpen={setIsVersionEditOpen}
        editingVersionRow={editingVersionRow}
        setEditingVersionRow={setEditingVersionRow}
        isSatisfactionViewOpen={isSatisfactionViewOpen}
        setIsSatisfactionViewOpen={setIsSatisfactionViewOpen}
        viewingSatisfactionRow={viewingSatisfactionRow}
        setViewingSatisfactionRow={setViewingSatisfactionRow}
        isParameterEditOpen={isParameterEditOpen}
        setIsParameterEditOpen={setIsParameterEditOpen}
        editingParameterRow={editingParameterRow}
        setEditingParameterRow={setEditingParameterRow}
        isDiscountManagementEditOpen={isDiscountManagementEditOpen}
        setIsDiscountManagementEditOpen={setIsDiscountManagementEditOpen}
        editingDiscountManagementRow={editingDiscountManagementRow}
        setEditingDiscountManagementRow={setEditingDiscountManagementRow}
        isAccessoryEditOpen={isAccessoryEditOpen}
        setIsAccessoryEditOpen={setIsAccessoryEditOpen}
        editingAccessoryRow={editingAccessoryRow}
        setEditingAccessoryRow={setEditingAccessoryRow}
        isAccessorySuperEditOpen={isAccessorySuperEditOpen}
        setIsAccessorySuperEditOpen={setIsAccessorySuperEditOpen}
        editingAccessorySuperRow={editingAccessorySuperRow}
        setEditingAccessorySuperRow={setEditingAccessorySuperRow}
        isHpSuperEditOpen={isHpSuperEditOpen}
        setIsHpSuperEditOpen={setIsHpSuperEditOpen}
        editingHpSuperRow={editingHpSuperRow}
        setEditingHpSuperRow={setEditingHpSuperRow}
        isHpEditOpen={isHpEditOpen}
        setIsHpEditOpen={setIsHpEditOpen}
        editingHpRow={editingHpRow}
        setEditingHpRow={setEditingHpRow}
        isTutorialEditOpen={isTutorialEditOpen}
        setIsTutorialEditOpen={setIsTutorialEditOpen}
        editingTutorialRow={editingTutorialRow}
        setEditingTutorialRow={setEditingTutorialRow}
        isAdminTutorialsLayout={isAdminTutorialsLayout}
        createDatasourceRequest={createDatasourceRequest}
        updateDatasourceRequest={updateDatasourceRequest}
        envNumeric={envLang.envNumeric}
        lang={envLang.lang}
        refetchDatasource={refetchDatasource}
        refetchFaq={refetchFaq}
        refetchAdminUsers={refetchAdminUsers}
        refetchVersions={refetchVersions}
      />

      <MenuDefPopup
        isOpen={isMenuDefCreateOpen}
        onClose={() => {
          setIsMenuDefCreateOpen(false);
          setEditingMenuDefRow(null);
        }}
        editingRow={editingMenuDefRow}
        onSaved={async () => {
          if (isDashboardLayout) {
            await refetchMenu();
            return;
          }
          await refetchDatasource();
        }}
      />
    </group>
  )
}
